/**
 * Passkey Status Tracking Utility
 * Generates the prop58 value for Adobe Analytics tracking
 * Format: passkeyCreated:passkeyCount:ssiToggle:biometricToggle
 *
 * Ticket: ACS-3073
 *
 * @example
 * // Passkey exists, 2 passkeys, SSI enabled, biometric enabled
 * getPasskeyStatusProp58() // Returns: "true:2:true:false"
 *
 * // No passkey data loaded yet
 * getPasskeyStatusProp58() // Returns: "not loaded:not loaded:true:false"
 */

import Cookies from 'utils/Cookies';
import Storage from 'utils/localStorage/Storage';
import store from 'store/Store';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { logPasskeyForDynatrace } from 'utils/passkeyDynatrace';

const NOT_LOADED = 'not loaded';

/**
 * Get the Stay Signed In (SSI) toggle value
 * Reads from isSSI cookie or localStorage
 * @returns {string} 'true' or 'false' (lowercase)
 */
const getSSIToggle = () => {
    try {
        // Try cookie first (primary source)
        const cookieValue = Cookies.read('isSSI');

        if (cookieValue !== undefined && cookieValue !== null) {
            return cookieValue === 'true' || cookieValue === true ? 'true' : 'false';
        }

        // Fallback to localStorage
        const localStorageValue = Storage.local.getItem('isSSI') || Storage.local.getItem(LOCAL_STORAGE.STAY_SIGN_IN);

        if (localStorageValue !== undefined && localStorageValue !== null) {
            return localStorageValue === true || localStorageValue === 'true' ? 'true' : 'false';
        }

        // Default to false if not found
        return 'false';
    } catch (error) {
        Sephora.logger.error('[Passkey Analytics] Error reading SSI toggle:', error);
        logPasskeyForDynatrace('Analytics', 'SSIToggle', error, 'analytics');

        return 'false';
    }
};

/**
 * Get the Use Biometric toggle value
 *
 * NOTE: "Use Biometric for sign in toggle" not yet implemented
 * Will be added in a later stage - currently defaults to 'false'
 *
 * @returns {string} 'true' or 'false' (lowercase)
 */
const getBiometricToggle = () => {
    // TODO: Implement when "Use Biometric for sign in toggle" feature is added
    return 'false';
};

/**
 * Get user passkeys from store
 * @returns {Object} { hasPasskey: boolean|string, count: number|string }
 */
const getPasskeyStatus = () => {
    try {
        const state = store.getState();
        const passkeys = state?.user?.passkeys;

        // If passkeys is undefined or null, data hasn't loaded yet
        if (!passkeys) {
            return { hasPasskey: NOT_LOADED, count: NOT_LOADED };
        }

        // Get count from passkeys array
        const count = passkeys.length;

        return {
            hasPasskey: count > 0 ? 'true' : 'false',
            count: count.toString()
        };
    } catch (error) {
        Sephora.logger.error('[Passkey Analytics] Error reading passkey status:', error);
        logPasskeyForDynatrace('Analytics', 'PasskeyStatus', error, 'analytics');

        return { hasPasskey: NOT_LOADED, count: NOT_LOADED };
    }
};

/**
 * Generate the prop58 value for passkey status tracking
 * All values are lowercase
 *
 * @returns {string} Format: "passkeyCreated:passkeyCount:ssiToggle:biometricToggle"
 */
const getPasskeyStatusProp58 = () => {
    if (Sephora.configurationSettings?.passkey?.isPasskeyEnabled === false) {
        return null;
    }

    try {
        const { hasPasskey, count } = getPasskeyStatus();
        const ssiToggle = getSSIToggle();
        const biometricToggle = getBiometricToggle();

        // Construct the value with colon separators
        const prop58Value = `${hasPasskey}:${count}:${ssiToggle}:${biometricToggle}`;

        // Ensure all values are lowercase
        return prop58Value.toLowerCase();
    } catch (error) {
        logPasskeyForDynatrace('Analytics', 'Prop58', error, 'analytics');

        // Return safe default on error
        return 'not loaded:not loaded:false:false';
    }
};

export default {
    getPasskeyStatusProp58,
    getSSIToggle,
    getBiometricToggle,
    getPasskeyStatus
};
